import { useState } from "react";
import { Save, Send, CheckCircle, AlertCircle, Loader2 } from "lucide-react";

interface DocToHtmlWithContentProps {
  htmlContent: string;
  isPublishedForm: boolean;
  errorText: string | null;
  showSuccess: boolean;
  mutationIsPending: boolean;
  submitFormPending: boolean;
  publishFormPending: boolean;
  isSaveDisabled: boolean;
  isSubmitEnabled: boolean;
  onSaveChanges: () => void;
  onSubmitForm: () => void;
  onPublishForm: () => void;
}

export function DocToHtmlWithContent({
  htmlContent,
  isPublishedForm,
  errorText,
  showSuccess,
  mutationIsPending,
  submitFormPending,
  publishFormPending,
  isSaveDisabled,
  isSubmitEnabled,
  onSaveChanges,
  onSubmitForm,
  onPublishForm,
}: DocToHtmlWithContentProps) {
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);
  const [isSuccessDismissed, setIsSuccessDismissed] = useState(false);

  const isFormDisabled = isPublishedForm || mutationIsPending || submitFormPending || publishFormPending;
  const allButtonsPending = mutationIsPending || submitFormPending || publishFormPending;

  return (
    <div className="flex flex-col flex-auto overflow-hidden">
      {/* Status Messages */}
      <div className="flex gap-2 px-6 py-3 bg-gray-50">
        {errorText && !isErrorDismissed && (
          <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg flex-auto">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-auto">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-xs text-red-700 mt-1">{errorText}</p>
            </div>
            <button
              onClick={() => setIsErrorDismissed(true)}
              className="text-red-600 hover:text-red-900 text-lg leading-none flex-shrink-0"
            >
              ×
            </button>
          </div>
        )}

        {showSuccess && !isSuccessDismissed && (
          <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg flex-auto">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-auto">
              <p className="text-sm font-medium text-green-900">Success</p>
              <p className="text-xs text-green-700 mt-1">Form saved successfully</p>
            </div>
            <button
              onClick={() => setIsSuccessDismissed(true)}
              className="text-green-600 hover:text-green-900 text-lg leading-none flex-shrink-0"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Form Container */}
      <div className="flex-auto overflow-auto">
        {htmlContent ? (
          <iframe
            srcDoc={htmlContent}
            title="Document Form"
            className="w-full h-full border-0"
            sandbox="allow-same-origin allow-popups"
            style={{ pointerEvents: isFormDisabled ? "none" : "auto", opacity: isFormDisabled ? 0.6 : 1 }}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-lg text-gray-500">Loading form...</div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="border-t border-gray-200 bg-white px-6 py-4 flex items-center justify-between gap-3">
        <div className="text-xs text-gray-500">
          {isFormDisabled && "Form is disabled"}
          {isSaveDisabled && " - Member assignment required to save"}
        </div>

        <div className="flex items-center gap-2">
          {/* Save Changes Button */}
          <button
            onClick={onSaveChanges}
            disabled={isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending || isFormDisabled}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isSaveDisabled || mutationIsPending || submitFormPending || publishFormPending || isFormDisabled
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800"
            }`}
          >
            {mutationIsPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>

          {/* Submit Form Button */}
          {!isPublishedForm && (
            <button
              onClick={onSubmitForm}
              disabled={
                !isSubmitEnabled ||
                isSaveDisabled ||
                mutationIsPending ||
                submitFormPending ||
                publishFormPending ||
                isFormDisabled
              }
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                !isSubmitEnabled ||
                isSaveDisabled ||
                mutationIsPending ||
                submitFormPending ||
                publishFormPending ||
                isFormDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700 active:bg-green-800"
              }`}
            >
              {submitFormPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Form
                </>
              )}
            </button>
          )}

          {/* Publish Form Button */}
          {isPublishedForm && (
            <button
              onClick={onPublishForm}
              disabled={allButtonsPending || isFormDisabled}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                allButtonsPending || isFormDisabled
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800"
              }`}
            >
              {publishFormPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Publish Form
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocToHtmlWithContent;
